import './tasksKanban.scss'
import _ from 'lodash'
import { Button, Card, CardContent, Dialog, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, Link, MenuItem, Select, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { queryClient } from 'queryClient';
import { Controller, useForm } from 'react-hook-form';
import pipelinesAdminService from 'services/admin/pipelinesAdminService';
import { AddPipelineForm } from './components/AddPipelineForm';
import { useQuery } from '@tanstack/react-query';
import { FaCalendarAlt, FaInfoCircle, FaLink, FaPlus, FaUser } from 'react-icons/fa';
import { toDate } from 'helpers/dateHelper';
import { TaskForm } from './components/TaskForm';
import { useGetTasks } from 'hooks/useGetTasks';
import { Task, TaskStatus } from 'interfaces/entities/task.model';
import tasksService from 'services/admin/tasksService';
import { useTranslation } from 'react-i18next';
import LeadFormWrapper from '../Leads/LeadForm';
import { Lead } from 'services/models/Lead';

const statuses = [
    { id: TaskStatus.TODO, name: 'TODO' },
    { id: TaskStatus.IN_PROGRESS, name: 'IN_PROGRESS' },
    { id: TaskStatus.DONE, name: 'DONE' },
]


const getDefaultPipelineID = (pipelines: any[]) => {
    const storedPipelineID = localStorage.getItem('taskPipelineID') ? Number(localStorage.getItem('taskPipelineID')) : null
    if (storedPipelineID) {
        const pipeline = pipelines.find((p: any) => p.id === storedPipelineID)
        if (pipeline) {
            return pipeline.id
        }
    }
    return _.get(pipelines, '[0].id')
}

const TasksKanban = ({
    pipelines,
}: any) => {
    const { t } = useTranslation()
    const [dragOverStageType, setDragOverStageType] = useState<string | null>(null)
    const [draggedTaskID, setDraggedTaskID] = useState<any>(null)

    const queryFormMethods = useForm({
        defaultValues: {
            pipelineID: getDefaultPipelineID(pipelines),
        },
        mode: 'onChange',
    })

    const pipelineID = queryFormMethods.watch('pipelineID')
    const pipeline = pipelines?.find((p: any) => p.id === pipelineID)

    const { data: tasksResponse, refetch: refetchTasks } = useGetTasks({
        pipelineID,
    })

    // Dialogs
    const [isOpenTaskDetail, setIsOpenTaskDetail] = useState(false)
    const [isOpenAddPipeline, setIsOpenAddPipeline] = useState(false)
    const [isSelectPipelineOpen, setIsSelectPipelineOpen] = useState(false)

    // Dialog: Форма сделки
    const [isOpenLeadDetail, setIsOpenLeadDetail] = useState(false)

    const [task, setTask] = useState<Task | null>(null)

    const [leadID, setLeadID] = useState<number | null>(null)

    useEffect(() => {
        localStorage.setItem('taskPipelineID', pipelineID)
    }, [pipelineID])


    const submitTask = (taskInput: { isNew: boolean, task: Task | null }) => {
        console.log("taskInput:", taskInput)
        refetchTasks()
        setIsOpenTaskDetail(false)
        setTask(null)
    }

    const handleAddPipelineSubmit = async (formData: any) => {
        console.log("add pipeline", formData)
        try {
            const { data: { pipeline } } = await pipelinesAdminService.post(formData)
            console.log("pipeline:", pipeline)
            toast.success("Воронка добавлена успешно!")

            // Добавляем воронку в кеш
            queryClient.setQueryData(['pipelines'], (old: any) => {
                return [...old, pipeline]
            })
        } catch (error) {
            toast.error("Ошибка при добавлении воронки!")
            console.error(error)
        } finally {
            setIsOpenAddPipeline(false)
        }
    }


    const openLeadDetailsDialog = () => {
        setIsOpenTaskDetail(true)
        setTask(null)
    }

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, task: Task) => {
        setDraggedTaskID(task?.id)
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('application/json', JSON.stringify({ taskID: task?.id, status: task?.status }))
        e.dataTransfer.setData('text/plain', task?.id?.toString() || '')
    }

    const handleDragEnd = () => {
        setDraggedTaskID(null)
        setDragOverStageType(null)
    }

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>, stageType: string) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        setDragOverStageType(stageType)
    }

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>, stageType: string) => {
        e.preventDefault()
        setDragOverStageType(stageType)
    }

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setDragOverStageType(null)
        }
    }

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>, status: string) => {
        e.preventDefault()
        setDragOverStageType(null)
        const taskID = e.dataTransfer.getData('text/plain')
        console.log("handleDrop: taskID:", taskID)

        const task = tasksResponse?.rows?.find((t: Task) => t.id === Number(taskID))
        console.log("handleDrop: task:", {
            tasksResponse,
            task
        })
        if (task) {
            await tasksService.patch(task.id, { status: status } as any)

            queryClient.setQueriesData({ queryKey: ['tasks'] }, (old: any) => {
                return {
                    ...old,
                    data: old.data.map((t: Task) => t.id === task.id ? { ...t, status: status } : t)
                }
            })
        }
    }

    const onCloseLeadDialog = (updatedLeadOrNewLead: { isNew: boolean, lead: Lead }) => {
        // Закрываем диалог
        setIsOpenLeadDetail(false)
        setLeadID(null)
    }

    return <div className='leads-kanban'>
        <div className='flex-row flex-space-between items-center'>
            <div className='flex gap-4 items-center'>
                <h3 className="mb-4">
                    Задачи
                    <Tooltip title="Задачи - Это действие, привязанное к объекту. Она отвечает на вопрос: «Что конкретно нужно сделать менеджеру, чтобы сделка не умерла?»">
                        <IconButton>
                            <FaInfoCircle size={14} />
                        </IconButton>
                    </Tooltip>
                </h3>
                {/* Pipelines(Воронки) */}
                <Controller control={queryFormMethods.control} name="pipelineID" render={({ field }) => {
                    return <FormControl>
                        <InputLabel>Доска</InputLabel>
                        <Select
                            label="Доска"
                            style={{
                                width: 200,
                                maxWidth: 200,
                            }}
                            open={isSelectPipelineOpen}
                            size="small"
                            onOpen={() => setIsSelectPipelineOpen(true)}
                            onClose={() => setIsSelectPipelineOpen(false)}
                            {...field}>
                            {pipelines?.map((pipeline: any) => (
                                <MenuItem key={pipeline.id} value={pipeline.id}>{pipeline.name}</MenuItem>
                            ))}
                            <hr className='my-2' />
                            <MenuItem onMouseDown={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setIsOpenAddPipeline(true)
                                setIsSelectPipelineOpen(false)
                            }}>+ Добавить доску</MenuItem>
                        </Select>
                    </FormControl>
                }} />
            </div>
            <Button size="small" variant="contained" color="primary" onClick={openLeadDetailsDialog}>
                <FaPlus size={12} className="mr-2" />
                Создать
            </Button>
        </div>
        <div className='board-container'>
            {statuses.map((status: { id: string, name: string }) => (
                <div key={status.id} className="column">
                    <div className="column-header">
                        <span className='text-md'>{t(`task.status.${status.id}`)}</span>
                        <span className='text-sm'>({tasksResponse?.rows?.filter((t: Task) => t.status === status.id)?.length || 0})</span>
                    </div>
                    <div className={`column-content ${dragOverStageType === status.id ? 'drag-over' : ''}`}
                        onDragOver={(e) => handleDragOver(e, status.id)}
                        onDragEnter={(e) => handleDragEnter(e, status.id)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, status.id)}
                    >
                        {tasksResponse?.rows?.filter((t: Task) => t.status === status.id)?.map((task: Task) => (
                            <div className={`card-wrapper ${draggedTaskID === task.id ? 'dragging' : ''}`}
                                key={task.id}
                                draggable={true}
                                onDragStart={(e) => handleDragStart(e, task)}
                                onDragEnd={handleDragEnd}
                            >
                                <Card>
                                    <CardContent>
                                        <div onClick={() => {
                                            setTask(task)
                                            setIsOpenTaskDetail(true)
                                        }}>
                                            <div className='card-title-item flex items-center gap-1 mb-2'>
                                                <span className='card-title-item-value text-sm font-bold'>{task.title} #{task.id}</span>
                                            </div>
                                            <div className='card-title-item flex items-center gap-1'>
                                                <FaCalendarAlt size={12} />
                                                <span className='card-title-item-value text-sm'>{toDate(task.dueDate)}</span>
                                            </div>
                                            <div className='card-title-item flex items-center gap-1'>
                                                <FaLink size={12} />
                                                <span className='card-title-item-value text-sm color-blue'>{task.leadID ? <span onClick={(e) => {
                                                    e.preventDefault()
                                                    e.stopPropagation()
                                                    setLeadID(task.leadID)
                                                    setIsOpenLeadDetail(true)
                                                }}>#{task.leadID}</span> : '-'}</span>
                                            </div>
                                            <div className='card-title-item flex items-center gap-1'>
                                                <FaUser size={12} />
                                                <span className='card-title-item-value text-sm'>{task.assignee?.name || task.assigneeID || '-'}</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
        {/* Task form */}
        <Dialog fullWidth maxWidth="xl" open={isOpenTaskDetail} onClose={() => { setIsOpenTaskDetail(false) }} sx={{
            '& .MuiDialog-paper': {
                height: '90vh',
            },
        }}>
            <DialogContent>
                <TaskForm task={task} onSave={submitTask} pipelines={pipelines} />
            </DialogContent>
        </Dialog>
        {/* Add deck dialog */}
        <Dialog fullWidth maxWidth="sm" open={isOpenAddPipeline} onClose={() => { setIsOpenAddPipeline(false) }}>
            <DialogTitle>Добавить доску</DialogTitle>
            <DialogContent>
                <AddPipelineForm onSubmit={handleAddPipelineSubmit} />
            </DialogContent>
        </Dialog>
        {/* Lead form */}
        <Dialog fullWidth maxWidth="xl" open={isOpenLeadDetail} onClose={() => { setIsOpenLeadDetail(false) }} sx={{
            '& .MuiDialog-paper': {
                height: '90vh',
            },
        }}>
            <DialogContent>
                <LeadFormWrapper
                    leadID={leadID || undefined}
                    onClose={onCloseLeadDialog}
                    selectedPipelineID={pipelineID}
                />
            </DialogContent>
        </Dialog>
    </div>
}

export default () => {
    const { data: pipelines, isLoading } = useQuery({
        queryKey: ['pipelines', 'tasks'],
        queryFn: async () => {
            const response: any = await pipelinesAdminService.getAll({
                type: "tasks"
            })
            return response.data
        },
        retry: false,
    })

    if (isLoading) return <div>Loading...</div>

    return <TasksKanban pipelines={pipelines} />
}
